import React, { useContext } from 'react';
import GroupedHeadCells from '@components/Table/GroupedHeadCells';
import { AvCheckbox } from '@fabric-ui/components';
import { GroupedHeadCell, HeadCellType, SortType } from '@fabric-ui/components/Table';
import { Filter } from '@fabric-ui/icons';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { TableContext } from './Context';
import TableHeadCell from './TableHeadCell';

const filterIcon = <Filter />;

interface EnhancedTableHeadProps {
  groupedHeadCells?: GroupedHeadCell;
  headCells: HeadCellType[];
  numSelected: number;
  onRequestSort?: (event, id, options) => void;
  onSelectAllClick: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  order?: SortType;
  orderBy: string;
  rowCount: number;
  multiSelect: boolean;
  onCancelColumn?: (v) => void;
  isSelectedAll: boolean;
  isLoading: boolean;
  disableSelectAll?: boolean;
}

const EnhancedTableHead: React.FC<EnhancedTableHeadProps> = ({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  headCells,
  multiSelect,
  groupedHeadCells,
  onCancelColumn,
  isSelectedAll,
  isLoading = false,
  disableSelectAll = false,
}) => {
  const { resizingColId } = useContext(TableContext);

  return (
    <TableHead
      sx={[
        { userSelect: 'none' },
        groupedHeadCells ? { position: 'sticky', top: '0' } : {},
        !!resizingColId && { '*': { cursor: 'col-resize !important' } },
      ]}>
      {groupedHeadCells && !!groupedHeadCells.cells.length && (
        <TableRow>
          <TableCell colSpan={1} sx={{ borderRight: `1px solid var(--color-border-base-secondary)`, ...groupedHeadCells.emptyCellStyle }} />
          <GroupedHeadCells headCells={groupedHeadCells} />
        </TableRow>
      )}
      <TableRow>
        {multiSelect ? (
          <TableCell padding="checkbox">
            <AvCheckbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              value={isSelectedAll || (rowCount > 0 && numSelected === rowCount)}
              disabled={isLoading || isSelectedAll || disableSelectAll}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all rows' }}
            />
          </TableCell>
        ) : null}
        {headCells.map(
          (
            {
              id,
              key,
              label,
              filter,
              sx = {},
              type,
              initWidth,
              disableSortBy,
              sort,
              disableFilter,
              isHighlighted,
              options,
              asyncGetOptions,
              getOptions,
            },
            index
          ) => {
            const titleLabel = (
              <>
                {filter ? filterIcon : null}
                <span>{label}</span>
              </>
            );
            const isActiveSort = orderBy === id || !!sort;
            const sortDirection = sort || (isActiveSort ? order : SortType.DESC);
            return (
              <TableHeadCell
                key={key ?? id}
                id={key ?? id}
                type={type}
                activeSort={isActiveSort}
                order={order}
                label={label}
                initWidth={initWidth}
                sx={sx}
                disableSortBy={disableSortBy}
                onRequestSort={onRequestSort}
                direction={sortDirection}
                onClick={event => onRequestSort?.(event, id, options)}
                titleLabel={titleLabel}
                onCancelColumn={onCancelColumn}
                index={index}
                disableFilter={disableFilter}
                isHighlighted={isHighlighted}
                asyncGetOptions={asyncGetOptions}
                getOptions={getOptions}
                options={options}
              />
            );
          }
        )}
      </TableRow>
    </TableHead>
  );
};

export default EnhancedTableHead;
